// El lado del navegador del tercer canal.
//
// Todo lo de aquí puede fallar de forma normal y esperada, y ninguna de esas
// formas es un error que enseñar: un navegador sin soporte, una persona que
// dijo que no, una instancia sin llaves, un iPhone que aún no ha instalado la
// aplicación. La respuesta a todas es la misma — el push no está disponible, la
// campana sigue funcionando, y nadie ve un mensaje rojo.

import { api } from '@general/ui-kit'

/**
 * ¿Puede este navegador recibir avisos con la aplicación cerrada?
 *
 * Las tres cosas hacen falta y ninguna se puede dar por hecha: Safari en iOS no
 * tiene `PushManager` hasta que la aplicación está instalada en la pantalla de
 * inicio, y un navegador en una pestaña privada tampoco.
 */
export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
}

/** `granted` · `denied` · `default` · `unsupported`. */
export function pushPermission() {
  if (!pushSupported()) return 'unsupported'
  return Notification.permission
}

/**
 * La llave pública de ESTA instancia, y si el canal existe siquiera.
 *
 * Se pregunta al servidor en vez de traerla en el build porque es de la
 * instancia y no del producto: el mismo `dist` corre en un cliente con push y
 * en otro sin él.
 */
export async function pushConfig() {
  try {
    return await api.get('/notifications/push/key')
  } catch {
    return { enabled: false, publicKey: null }
  }
}

/**
 * Pide permiso y da de alta este navegador.
 *
 * Devuelve el estado, nunca lanza: quien llama es un botón de una pantalla de
 * preferencias, y lo que necesita es saber qué pintar después.
 *
 * @returns {Promise<'subscribed'|'denied'|'unsupported'|'unavailable'|'failed'>}
 */
export async function enablePush() {
  if (!pushSupported()) return 'unsupported'

  const config = await pushConfig()
  if (!config.enabled || !config.publicKey) return 'unavailable'

  // El permiso se pide DESPUÉS de saber que la instancia tiene push. Al revés,
  // una instancia sin llaves quemaría el permiso del navegador para siempre:
  // un «no» de éstos no se vuelve a preguntar.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return 'denied'

  try {
    const registration = await navigator.serviceWorker.ready

    // Reusa la que ya haya. Volver a suscribirse con otra llave devolvería un
    // endpoint distinto y dejaría el anterior vivo en la base de datos: el
    // mismo móvil vibrando dos veces por aviso.
    const existing = await registration.pushManager.getSubscription()
    const subscription = existing ?? await registration.pushManager.subscribe({
      // Sin esto Chrome rechaza la suscripción: obliga a que todo push que
      // llegue se le enseñe a la persona, y no se pueda usar de baliza.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey),
    })

    const raw = subscription.toJSON()
    await api.post('/notifications/push/subscribe', {
      endpoint: raw.endpoint,
      keys: { p256dh: raw.keys?.p256dh, auth: raw.keys?.auth },
    })

    return 'subscribed'
  } catch {
    return 'failed'
  }
}

/** Baja de este navegador: en el servicio de push y en el servidor. */
export async function disablePush() {
  if (!pushSupported()) return 'unsupported'

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return 'unsubscribed'

    const endpoint = subscription.endpoint
    await subscription.unsubscribe()
    // Después de darla de baja en el navegador, y sin que un servidor caído lo
    // impida: lo que la persona ha pedido es dejar de recibir, y eso ya pasó.
    await api.post('/notifications/push/unsubscribe', { endpoint }).catch(() => {})

    return 'unsubscribed'
  } catch {
    return 'failed'
  }
}

/**
 * La baja al cerrar sesión. La suscripción es del navegador y no de la
 * persona: si se queda, la tablet de la cuadrilla sigue sonando con los avisos
 * de quien salió, aunque dentro esté otro o no esté nadie.
 *
 * Distinta de `disablePush` en dos cosas: primero el servidor (corre antes de
 * revocar el token, y es lo que lo necesita) y sin `serviceWorker.ready`, que
 * no resuelve nunca donde no hay worker registrado (desarrollo): cerrar sesión
 * no puede esperar a algo que no va a llegar.
 */
export async function releasePushOnSignOut() {
  if (!pushSupported() || Notification.permission !== 'granted') return 'none'
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = await registration?.pushManager.getSubscription()
    if (!subscription) return 'none'
    await api.post('/notifications/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {})
    await subscription.unsubscribe()
    return 'unsubscribed'
  } catch {
    return 'failed'
  }
}

/** ¿Está ESTE navegador suscrito ahora mismo? */
export async function pushSubscribed() {
  if (!pushSupported() || Notification.permission !== 'granted') return false
  try {
    const registration = await navigator.serviceWorker.ready
    return Boolean(await registration.pushManager.getSubscription())
  } catch {
    return false
  }
}

/**
 * La llave viaja como base64url y `applicationServerKey` la quiere en bytes.
 *
 * Es la conversión que todo el mundo copia de la documentación de la API, y va
 * aquí escrita entera porque una librería para doce líneas es una dependencia
 * que hay que aprobar, actualizar y auditar (principio 2).
 */
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(normalised)
  const bytes = new Uint8Array(raw.length)
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index)
  return bytes
}
